import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useSetAtom } from "jotai";
import { useResetAtom } from "jotai/utils";
import { useState, type JSX } from "react";
import { z } from "zod";

import IllustrationCalendar from "@repo/icons/colored/IllustrationCalendar";
import App from "@repo/icons/default/App";
import ExclamationCircle from "@repo/icons/default/ExclamationCircle";
import Medical from "@repo/icons/default/Medical";
import Share from "@repo/icons/default/Share";

import { WelcomeMeetingFooter } from "@/components/onboarding/welcome-meeting-footer";
import {
  MessageDrawerActionButton,
  MessageDrawerActions,
  MessageDrawerBody,
  MessageDrawerRoot,
  MessageDrawerTitle,
} from "@/components/ui/drawer/message-drawer";
import {
  EmptyStateV2Description,
  EmptyStateV2Root,
  EmptyStateV2Title,
} from "@/components/ui/empty-state/empty-state-v2";
import { FlowHeader } from "@/components/ui/flow-header/flow-header";
import { Page } from "@/components/ui/layout/page/page";
import { RoundedLayout } from "@/components/ui/layout/rounded-layout";
import { useWelcomeMeetingHomeBanner } from "@/hooks/use-welcome-meeting-home-banner";
import { welcomeMeetingAtom } from "@/lib/atoms/welcome-meeting-atom";

const welcomeMeetingIndexSearchSchema = z.object({
  action: z.string().optional(),
});

const WelcomeMeetingPage = (): JSX.Element => {
  const navigate = useNavigate();
  const resetForm = useResetAtom(welcomeMeetingAtom);
  const setWelcomeMeetingForm = useSetAtom(welcomeMeetingAtom);
  const { action } = Route.useSearch();

  const [showRescheduleConfirm, setShowRescheduleConfirm] = useState(false);

  // Detecta se o membro já tem uma reunião de onboarding ativa e futura, para
  // evitar agendamento duplicado quando o link (ex.: WhatsApp) cai aqui sem o
  // parâmetro action=reschedule.
  const {
    shouldShow: hasActiveMeeting,
    isConsulting,
    unitName,
    formattedDate,
    formattedTime,
    isLoading,
  } = useWelcomeMeetingHomeBanner();

  const startFreshSchedule = (): void => {
    resetForm();
    void navigate({ to: "/onboarding/welcome-meeting/format" });
  };

  const handleSchedule = (): void => {
    // Reagendamento intencional (fluxo interno já passa action=reschedule).
    if (action === "reschedule") {
      resetForm();
      setWelcomeMeetingForm((prev) => ({
        ...prev,
        isRescheduling: true,
      }));
      void navigate({ to: "/onboarding/welcome-meeting/format" });
      return;
    }

    // Já existe reunião ativa: confirma antes de deixar criar uma nova.
    if (hasActiveMeeting) {
      setShowRescheduleConfirm(true);
      return;
    }

    startFreshSchedule();
  };

  // Espelha o handleReschedule de details.tsx: encaminha para o fluxo de
  // reagendamento existente (cancela o anterior e reaproveita as respostas da
  // consultoria em location.tsx).
  const handleConfirmReschedule = (): void => {
    resetForm();
    setWelcomeMeetingForm((prev) => ({
      ...prev,
      isRescheduling: true,
      selectedLocationCode: 0,
      selectedLocationName: "",
      selectedLocationAddress: "",
      date: "",
      timeSlot: null,
    }));
    void navigate({ to: "/onboarding/welcome-meeting/location" });
  };

  const meetingLabel = isConsulting ? "de consultoria" : "de boas vindas";
  const scheduledWhen = [
    unitName ? `na unidade ${unitName}` : null,
    formattedDate ? `no dia ${formattedDate}` : null,
    formattedTime ? `às ${formattedTime}` : null,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <RoundedLayout>
      <FlowHeader>
        <FlowHeader.Actions>
          <FlowHeader.BackButton
            onBack={() => {
              void navigate({ to: "/home" });
            }}
          />
        </FlowHeader.Actions>
      </FlowHeader>

      <Page
        className="flex flex-col justify-between pb-0 px-6"
        showCustomerIoContainer={false}
      >
        <EmptyStateV2Root className="gap-4 py-4">
          <IllustrationCalendar size={80} aria-hidden="true" />

          <div>
            <EmptyStateV2Title className="text-2xl font-medium">
              Vamos agendar a sua reunião de boas vindas
            </EmptyStateV2Title>
            <EmptyStateV2Description className="text-md">
              Este encontro foi pensado para te apoiar nos primeiros passos e garantir uma
              ótima experiência desde o início.
            </EmptyStateV2Description>
            <EmptyStateV2Description className="text-md">
              Durante a reunião, você vai conhecer:
            </EmptyStateV2Description>
          </div>

          <div className="flex flex-col gap-3 rounded-lg bg-neutral-50 p-4">
            <div className="flex items-start gap-2 text-sm text-neutral-700">
              <App
                size={24}
                className="fill-primary-400 shrink-0 mt-0.5"
                aria-hidden="true"
              />
              <span>Ferramentas e processos do sistema e aplicativo</span>
            </div>
            <div className="flex items-start gap-2 text-sm text-neutral-700">
              <Medical
                size={24}
                className="fill-primary-400 shrink-0 mt-0.5"
                aria-hidden="true"
              />
              <span>Orientações para realizar os seus atendimentos</span>
            </div>
            <div className="flex items-start gap-2 text-sm text-neutral-700">
              <Share
                size={24}
                className="fill-primary-400 shrink-0 mt-0.5"
                aria-hidden="true"
              />
              <span>Programas e benefícios da Livance</span>
            </div>
          </div>
        </EmptyStateV2Root>

        <WelcomeMeetingFooter
          onSubmit={handleSchedule}
          submitLabel="Agendar reunião"
          // O reagendamento direto (action=reschedule) não depende da checagem de
          // agendamento ativo, então não precisa esperar a query carregar.
          disabled={action !== "reschedule" && isLoading}
        />
      </Page>

      <MessageDrawerRoot
        icon={ExclamationCircle}
        variant="secondary"
        open={showRescheduleConfirm}
        setOpen={setShowRescheduleConfirm}
      >
        <MessageDrawerTitle>
          {`Você já tem uma reunião ${meetingLabel} agendada`}
        </MessageDrawerTitle>
        <MessageDrawerBody>
          {scheduledWhen
            ? `Sua reunião está marcada ${scheduledWhen}. Tem certeza que deseja reagendar? O horário atual será cancelado.`
            : "Você já tem uma reunião agendada. Tem certeza que deseja reagendar? O horário atual será cancelado."}
        </MessageDrawerBody>
        <MessageDrawerActions>
          <MessageDrawerActionButton onClick={handleConfirmReschedule}>
            Sim, quero reagendar
          </MessageDrawerActionButton>
          <MessageDrawerActionButton
            secondary
            onClick={() => {
              void navigate({ to: "/onboarding/welcome-meeting/details" });
            }}
          >
            Ver reunião agendada
          </MessageDrawerActionButton>
        </MessageDrawerActions>
      </MessageDrawerRoot>
    </RoundedLayout>
  );
};

export const Route = createFileRoute("/onboarding/welcome-meeting/")({
  validateSearch: welcomeMeetingIndexSearchSchema,
  component: WelcomeMeetingPage,
});
